// Dog profiles + grooming history — persisted in localStorage.
// Same shape a future backend / MongoDB collection would return.

const DOGS_KEY = "dogstyle_dogs_v1";

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota — ignore */
  }
}

function uid() {
  return `d_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Dog:
 * {
 *   id, createdAt, updatedAt,
 *   name, breed, breedUnknown, age,
 *   originalPhoto,        // dataURL of primary photo (last uploaded)
 *   photos: [dataURL],    // reserved for multi-photo future
 *   history: [HistoryEntry]
 * }
 *
 * HistoryEntry:
 * {
 *   id, createdAt,
 *   style, bodyLength, faceStyle, earStyle, tailStyle,
 *   customInstructions,
 *   previewImage,         // dataURL of the AI/mock preview
 * }
 */

export function listDogs() {
  const arr = readJSON(DOGS_KEY, []);
  if (!Array.isArray(arr)) return [];
  // Self-heal any legacy entries missing an id (protects against pre-fix data).
  let changed = false;
  const healed = arr.map((d) => {
    if (d && !d.id) {
      changed = true;
      return { ...d, id: uid() };
    }
    return d;
  });
  if (changed) writeJSON(DOGS_KEY, healed);
  return healed;
}

export function getDog(id) {
  return listDogs().find((d) => d.id === id) || null;
}

export function saveDog(input) {
  const dogs = listDogs();
  const now = Date.now();
  const { id: inputId, ...rest } = input || {};
  if (inputId) {
    const idx = dogs.findIndex((d) => d.id === inputId);
    if (idx >= 0) {
      dogs[idx] = { ...dogs[idx], ...rest, updatedAt: now };
      writeJSON(DOGS_KEY, dogs);
      return dogs[idx];
    }
  }
  const dog = {
    id: uid(),
    createdAt: now,
    updatedAt: now,
    name: "",
    breed: "",
    breedUnknown: false,
    age: "",
    originalPhoto: null,
    photos: [],
    history: [],
    ...rest,
  };
  dogs.unshift(dog);
  writeJSON(DOGS_KEY, dogs);
  return dog;
}

export function deleteDog(id) {
  const dogs = listDogs().filter((d) => d.id !== id);
  writeJSON(DOGS_KEY, dogs);
}

export function addHistoryEntry(dogId, entry) {
  const dogs = listDogs();
  const idx = dogs.findIndex((d) => d.id === dogId);
  if (idx < 0) return null;
  const hist = {
    id: `h_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    createdAt: Date.now(),
    ...entry,
  };
  dogs[idx].history = [hist, ...(dogs[idx].history || [])];
  dogs[idx].updatedAt = Date.now();
  writeJSON(DOGS_KEY, dogs);
  return hist;
}

export function updateHistoryEntry(dogId, entryId, patch) {
  const dogs = listDogs();
  const idx = dogs.findIndex((d) => d.id === dogId);
  if (idx < 0) return null;
  const history = (dogs[idx].history || []).map((h) => (h.id === entryId ? { ...h, ...patch } : h));
  dogs[idx] = { ...dogs[idx], history, updatedAt: Date.now() };
  writeJSON(DOGS_KEY, dogs);
  return history.find((h) => h.id === entryId) || null;
}

export function toggleFavorite(dogId, entryId) {
  const dogs = listDogs();
  const idx = dogs.findIndex((d) => d.id === dogId);
  if (idx < 0) return null;
  const history = (dogs[idx].history || []).map((h) =>
    h.id === entryId ? { ...h, favorite: !h.favorite } : h,
  );
  dogs[idx] = { ...dogs[idx], history, updatedAt: Date.now() };
  writeJSON(DOGS_KEY, dogs);
  return history.find((h) => h.id === entryId) || null;
}

// Favourites pinned to top; otherwise preserves existing (newest-first) order.
export function sortHistory(history) {
  return [...(history || [])].sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
}

export function findDogByFingerprint({ name, breed, originalPhoto }) {
  const dogs = listDogs();
  // Prefer name + breed match first
  const match =
    dogs.find(
      (d) =>
        name &&
        d.name &&
        d.name.trim().toLowerCase() === name.trim().toLowerCase() &&
        (!breed || d.breed?.toLowerCase() === breed.toLowerCase()),
    ) ||
    dogs.find((d) => d.originalPhoto && d.originalPhoto === originalPhoto);
  return match || null;
}
